import {
  MoreHorizontal,
  Plus,
  Settings,
  Wallet as WalletIcon,
} from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import {
  useUpdateWallet,
  useWalletByCustomerId,
  useWalletTransactions,
} from '../../hooks/useWallet'
import type { Customer } from '../../services/customerService'
import { walletService } from '../../services/walletService'
import type { CustomerProfile } from '../../types/customerDetail.types'
import type { WalletTransaction } from '../../types/wallet.types'
import { AddCreditsDialog } from '../wallet/AddCreditsDialog'
import { WalletBalance } from '../wallet/WalletBalance'
import { WalletCreationDrawer } from '../wallet/WalletCreationDrawer'
import { WalletTransactions } from '../wallet/WalletTransactions'

interface WalletTabProps {
  customer: Customer
}

export function WalletTab({ customer }: WalletTabProps) {
  // React Query hooks for data fetching
  const {
    data: wallet,
    isLoading: walletLoading,
    error: walletError,
    refetch: refetchWallet,
  } = useWalletByCustomerId(customer.id)
  const { data: transactionsData, isLoading: transactionsLoading } =
    useWalletTransactions(wallet?.id || '', 1, 20)

  // Mutation hooks
  const updateWalletMutation = useUpdateWallet()

  // UI state
  const [selectedTransaction, setSelectedTransaction] =
    useState<WalletTransaction | null>(null)
  const [isAddingCredit, setIsAddingCredit] = useState(false)

  const loading = walletLoading || transactionsLoading

  const formatCurrency = (amount: number, currency = 'USD') => {
    return walletService.formatAmount(amount, currency)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Handler functions
  const handleViewTransactionDetails = (transaction: WalletTransaction) => {
    setSelectedTransaction(transaction)
  }

  const handleStatusChange = async (
    newStatus: 'active' | 'suspended' | 'closed'
  ) => {
    if (!wallet) {
      return
    }

    try {
      await updateWalletMutation.mutateAsync({
        walletId: wallet.id,
        updateData: {
          status: newStatus,
        },
      })
      // Refetch wallet data to show updated status
      refetchWallet()
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  const getStatusActions = () => {
    if (!wallet) {
      return []
    }

    const actions = []

    switch (wallet.status) {
      case 'active':
        actions.push(
          {
            label: 'Suspend Wallet',
            action: () => handleStatusChange('suspended'),
            disabled: false,
          },
          {
            label: 'Close Wallet',
            action: () => handleStatusChange('closed'),
            disabled: false,
          }
        )
        break
      case 'suspended':
        actions.push(
          {
            label: 'Activate Wallet',
            action: () => handleStatusChange('active'),
            disabled: false,
          },
          {
            label: 'Close Wallet',
            action: () => handleStatusChange('closed'),
            disabled: false,
          }
        )
        break
      case 'closed':
        actions.push({
          label: 'Reactivate Wallet',
          action: () => handleStatusChange('active'),
          disabled: false,
        })
        break
    }

    return actions
  }

  if (loading) {
    return (
      <div className='space-y-6'>
        {/* Header Skeleton */}
        <div className='flex justify-between items-center'>
          <div className='animate-pulse'>
            <div className='h-8 bg-slate-300 dark:bg-slate-600 rounded w-48 mb-2'></div>
            <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-80'></div>
          </div>
          <div className='animate-pulse flex gap-2'>
            <div className='h-10 bg-slate-300 dark:bg-slate-600 rounded w-32'></div>
            <div className='h-10 bg-slate-300 dark:bg-slate-600 rounded w-24'></div>
          </div>
        </div>

        {/* Cards Skeleton */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
          {Array.from({ length: 3 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <Card key={i} className='bg-transparent border'>
              <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                <div className='animate-pulse'>
                  <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-20'></div>
                </div>
                <div className='animate-pulse'>
                  <div className='h-4 w-4 bg-slate-300 dark:bg-slate-600 rounded'></div>
                </div>
              </CardHeader>
              <CardContent>
                <div className='animate-pulse'>
                  <div className='h-8 bg-slate-300 dark:bg-slate-600 rounded w-24 mb-1'></div>
                  <div className='h-3 bg-slate-300 dark:bg-slate-600 rounded w-16'></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Table Skeleton */}
        <div className='bg-transparent border'>
          <div className='animate-pulse space-y-4 p-6'>
            <div className='grid grid-cols-6 gap-4 pb-4 border-b'>
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                  key={i}
                  className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                ></div>
              ))}
            </div>
            {Array.from({ length: 5 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
              <div key={i} className='grid grid-cols-6 gap-4 py-3'>
                {Array.from({ length: 6 }).map((_, j) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    key={j}
                    className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                  ></div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (walletError) {
    // Check if it's a 404 error (no wallet found) - treat as no wallet rather than error
    const is404 =
      walletError.message?.includes('404') ||
      walletError.message?.includes('Not Found') ||
      walletError.message?.includes('status code 404')

    if (is404) {
      return (
        <div className='text-center py-8'>
          <div className='text-muted-foreground mb-4'>
            No wallet found for this customer
          </div>
          <WalletCreationDrawer
            customer={customer as unknown as CustomerProfile}
            onCreated={() => {
              // Refetch wallet data after creation
              refetchWallet()
            }}
          />
        </div>
      )
    }

    // For other errors, show error state
    return (
      <div className='text-center py-12 text-muted-foreground'>
        <WalletIcon className='w-12 h-12 mx-auto mb-4 text-muted-foreground/50' />
        <h3 className='text-lg font-medium mb-2'>Error loading wallet</h3>
        <p className='text-sm'>
          {walletError instanceof Error
            ? walletError.message
            : 'Unable to load wallet information for this customer.'}
        </p>
      </div>
    )
  }

  if (!wallet) {
    return (
      <div className='text-center py-8'>
        <div className='text-muted-foreground mb-4'>
          No wallet found for this customer
        </div>
        <WalletCreationDrawer
          customer={customer as unknown as CustomerProfile}
          onCreated={() => {
            // Refetch wallet data after creation
            refetchWallet()
          }}
        />
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-center'>
        <div className='flex items-center gap-3'>
          <h2 className='text-2xl font-bold'>Wallet</h2>
          <div className='flex items-center gap-2'>
            <Badge
              variant={
                wallet.status === 'active'
                  ? 'default'
                  : wallet.status === 'suspended'
                    ? 'destructive'
                    : 'secondary'
              }
              className={
                wallet.status === 'active'
                  ? 'bg-green-100 text-green-800 border-green-200'
                  : ''
              }
            >
              {walletService.getWalletStatusText(wallet.status)}
            </Badge>
            <Badge
              variant='secondary'
              className='bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-700'
            >
              1 Credit ={' '}
              {formatCurrency(wallet.conversion_rate, wallet.currency)}
            </Badge>
            {/* Status Actions Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                  <MoreHorizontal className='w-4 h-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' className='w-48'>
                {getStatusActions().map((action, index) => (
                  <DropdownMenuItem
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    key={index}
                    onClick={action.action}
                    disabled={action.disabled || updateWalletMutation.isPending}
                  >
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className='flex gap-2'>
          <Button className='gap-2' onClick={() => setIsAddingCredit(true)}>
            <Plus className='w-4 h-4' />
            Topup Wallet
          </Button>

          <AddCreditsDialog
            wallet={wallet as never}
            customerName={customer.name}
            open={isAddingCredit}
            onOpenChange={setIsAddingCredit}
          />

          <WalletCreationDrawer
            customer={customer as unknown as CustomerProfile}
            mode='update'
            existingWallet={
              wallet as unknown as import('../../types/wallet.types').Wallet
            }
            onUpdated={() => {
              // Refetch wallet data after update
              refetchWallet()
            }}
            trigger={
              <Button variant='outline' className='gap-2'>
                <Settings className='w-4 h-4' />
                Settings
              </Button>
            }
          />
        </div>
      </div>

      {/* Balance Cards */}
      <WalletBalance
        wallet={wallet as unknown as import('../../types/wallet.types').Wallet}
      />

      {/* Transactions Table */}
      <WalletTransactions
        wallet={wallet as unknown as import('../../types/wallet.types').Wallet}
        transactionsData={
          transactionsData as unknown as
            | {
                data: WalletTransaction[]
                meta: {
                  page: number
                  limit: number
                  total: number
                  pages: number
                  has_next: boolean
                  has_prev: boolean
                }
              }
            | undefined
        }
        isLoading={transactionsLoading}
        onViewTransactionDetails={handleViewTransactionDetails}
      />

      {/* Transaction Details Dialog */}
      {selectedTransaction && (
        <Dialog
          open={!!selectedTransaction}
          onOpenChange={() => setSelectedTransaction(null)}
        >
          <DialogContent className='max-w-3xl'>
            <DialogHeader>
              <DialogTitle>Transaction Details</DialogTitle>
              <DialogDescription>
                Complete transaction information and history
              </DialogDescription>
            </DialogHeader>
            <div className='space-y-4'>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <span className='text-sm font-medium'>Transaction ID</span>
                  <p className='text-sm text-muted-foreground'>
                    {selectedTransaction.id}
                  </p>
                </div>
                <div>
                  <span className='text-sm font-medium'>Type</span>
                  <p className='text-sm text-muted-foreground'>
                    {selectedTransaction.type.charAt(0).toUpperCase() +
                      selectedTransaction.type.slice(1)}
                  </p>
                </div>
                <div>
                  <span className='text-sm font-medium'>Amount</span>
                  <p className='text-sm text-muted-foreground'>
                    {formatCurrency(
                      selectedTransaction.amount,
                      selectedTransaction.currency
                    )}
                  </p>
                </div>
                <div>
                  <span className='text-sm font-medium'>Created</span>
                  <p className='text-sm text-muted-foreground'>
                    {formatDate(selectedTransaction.created_at)}
                  </p>
                </div>
                <div>
                  <span className='text-sm font-medium'>Balance After</span>
                  <p className='text-sm text-muted-foreground'>
                    {formatCurrency(
                      selectedTransaction.balance_after,
                      selectedTransaction.currency
                    )}
                  </p>
                </div>
                <div>
                  <span className='text-sm font-medium'>Customer ID</span>
                  <p className='text-sm text-muted-foreground font-mono'>
                    {selectedTransaction.customer_id}
                  </p>
                </div>
              </div>

              {selectedTransaction.description && (
                <div>
                  <span className='text-sm font-medium'>Description</span>
                  <p className='text-sm text-muted-foreground'>
                    {selectedTransaction.description}
                  </p>
                </div>
              )}

              {selectedTransaction.reference_id && (
                <div>
                  <span className='text-sm font-medium'>Reference</span>
                  <p className='text-sm text-muted-foreground'>
                    {selectedTransaction.reference_type?.toUpperCase()} #
                    {selectedTransaction.reference_id}
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
